export interface PrintTriangle {
  groupId?: number;
  v0: { x: number; y: number; z: number };
  v1: { x: number; y: number; z: number };
  v2: { x: number; y: number; z: number };
}

export interface PrintEstimate {
  scope: string;
  volumeCm3: number;
  materialGrams: number;
  printGrams: number;
  wasteGrams: number;
  materialLengthM: number;
  printLengthM: number;
  wasteLengthM: number;
  timeMinutes: number;
  layers: number;
  layerHeight: number;
  infill: number;
  colorCount: number;
  purgeGrams: number;
  preset: PrintPreset;
  printer: PrintPrinter;
  wasteMode: PrintWasteMode;
}

export type PrintPreset = 'bambu' | 'prusa' | 'orca';
export type PrintPrinter = 'bambu' | 'prusa' | 'snapmaker';
export type PrintWasteMode = 'orca-layer' | 'simple';

export interface PrintEstimateOptions {
  layerHeight?: 0.12 | 0.16 | 0.2;
  infill?: 0.05 | 0.1 | 0.15;
  colorCount?: number;
  preset?: PrintPreset;
  printer?: PrintPrinter;
  wasteMode?: PrintWasteMode;
}

const SLICER_PRESETS: Record<PrintPreset, { flow: number; purgePerLayerTransition: number }> = {
  bambu: { flow: 2.8, purgePerLayerTransition: 28 },
  prusa: { flow: 2.5, purgePerLayerTransition: 24 },
  // Calibrated to Orca prime-tower output: ~3.98 g at 38 layers and 4 colors.
  orca: { flow: 2.6, purgePerLayerTransition: 28 },
};

const PRINTER_PROFILES: Record<PrintPrinter, { startup: number; layerOverhead: number; purge: number; colorChange: number }> = {
  bambu: { startup: 85, layerOverhead: 1.0, purge: 220, colorChange: 12 },
  prusa: { startup: 95, layerOverhead: 1.2, purge: 160, colorChange: 10 },
  snapmaker: { startup: 100, layerOverhead: 1.2, purge: 180, colorChange: 12 },
};

// PLA at 1.24 g/cm³ through a 1.75 mm filament: about 2.98 g per metre.
const PLA_FILAMENT_GRAMS_PER_METER = Math.PI * (1.75 / 20) ** 2 * 100 * 1.24;

/** Estimate PLA use and print duration from a triangle mesh at 0.2 mm layers. */
export function estimatePrint(
  triangles: readonly PrintTriangle[],
  selectedGroupIds: ReadonlySet<number> = new Set<number>(),
  options: PrintEstimateOptions = {},
): PrintEstimate | null {
  if (triangles.length === 0) return null;

  const layerHeight = options.layerHeight ?? 0.2;
  const infill = options.infill ?? 0.15;
  const colorCount = Math.max(1, Math.floor(options.colorCount ?? 1));
  const preset = options.preset ?? 'orca';
  const printer = options.printer ?? 'snapmaker';
  const wasteMode = options.wasteMode ?? 'orca-layer';
  const slicer = SLICER_PRESETS[preset];
  const machine = PRINTER_PROFILES[printer];
  const isSelection = selectedGroupIds.size > 0;
  const groups = new Map<number, { signedVolume: number; area: number; minZ: number; maxZ: number }>();
  for (const tri of triangles) {
    const gid = tri.groupId ?? 0;
    if (isSelection && !selectedGroupIds.has(gid)) continue;
    let stats = groups.get(gid);
    if (!stats) {
      stats = { signedVolume: 0, area: 0, minZ: Infinity, maxZ: -Infinity };
      groups.set(gid, stats);
    }
    const { v0, v1, v2 } = tri;
    const cx = v1.y * v2.z - v1.z * v2.y;
    const cy = v1.z * v2.x - v1.x * v2.z;
    const cz = v1.x * v2.y - v1.y * v2.x;
    stats.signedVolume += (v0.x * cx + v0.y * cy + v0.z * cz) / 6;
    stats.area += 0.5 * Math.hypot(
      (v1.y - v0.y) * (v2.z - v0.z) - (v1.z - v0.z) * (v2.y - v0.y),
      (v1.z - v0.z) * (v2.x - v0.x) - (v1.x - v0.x) * (v2.z - v0.z),
      (v1.x - v0.x) * (v2.y - v0.y) - (v1.y - v0.y) * (v2.x - v0.x),
    );
    stats.minZ = Math.min(stats.minZ, v0.z, v1.z, v2.z);
    stats.maxZ = Math.max(stats.maxZ, v0.z, v1.z, v2.z);
  }
  if (groups.size === 0) return null;

  let modelVolume = 0;
  let surfaceArea = 0;
  let layerCount = 0;
  for (const stats of groups.values()) {
    modelVolume += Math.abs(stats.signedVolume);
    surfaceArea += stats.area;
    layerCount = Math.max(layerCount, Math.ceil((stats.maxZ - stats.minZ) / layerHeight));
  }

  // Approximate two 0.45 mm walls plus 15% infill in the remaining enclosed volume.
  const shellVolume = Math.min(modelVolume, surfaceArea * 0.45 * 1.5);
  const extrudedVolume = shellVolume + Math.max(0, modelVolume - shellVolume) * infill;
  const purgeTransitions = colorCount > 1 ? colorCount - 1 : 0;
  const purgeVolume = wasteMode === 'orca-layer'
    ? layerCount * purgeTransitions * slicer.purgePerLayerTransition
    : purgeTransitions * machine.purge;
  const totalExtrudedVolume = extrudedVolume + purgeVolume;
  const printGrams = (extrudedVolume / 1000) * 1.24;
  const wasteGrams = (purgeVolume / 1000) * 1.24;
  const materialGrams = printGrams + wasteGrams;
  const printLengthM = printGrams / PLA_FILAMENT_GRAMS_PER_METER;
  const wasteLengthM = wasteGrams / PLA_FILAMENT_GRAMS_PER_METER;
  const materialLengthM = materialGrams / PLA_FILAMENT_GRAMS_PER_METER;
  // Effective flow ~2.6 mm³/s for a 0.4 mm nozzle / 0.2 mm layer, plus layer overhead.
  const timeMinutes = (totalExtrudedVolume / slicer.flow + layerCount * machine.layerOverhead + machine.startup + purgeTransitions * machine.colorChange) / 60;
  const scope = isSelection
    ? (selectedGroupIds.size === 1 ? 'Đối tượng đã chọn' : `${selectedGroupIds.size} phần đã chọn`)
    : 'Toàn bộ mô hình';

  return {
    scope,
    volumeCm3: extrudedVolume / 1000,
    materialGrams,
    printGrams,
    wasteGrams,
    materialLengthM,
    printLengthM,
    wasteLengthM,
    timeMinutes,
    layers: layerCount,
    layerHeight,
    infill,
    colorCount,
    purgeGrams: wasteGrams,
    preset,
    printer,
    wasteMode,
  };
}
